import { type LGraphNode, isComboWidget } from '@comfyorg/litegraph'
import type {
  IBaseWidget,
  IComboWidget,
  IStringWidget
} from '@comfyorg/litegraph/dist/types/widgets'

import { useBooleanWidget } from '@/composables/widgets/useBooleanWidget'
import { useComboWidget } from '@/composables/widgets/useComboWidget'
import { useFloatWidget } from '@/composables/widgets/useFloatWidget'
import { useImageUploadWidget } from '@/composables/widgets/useImageUploadWidget'
import { useIntWidget } from '@/composables/widgets/useIntWidget'
import { useMarkdownWidget } from '@/composables/widgets/useMarkdownWidget'
import { useStringWidget } from '@/composables/widgets/useStringWidget'
import { t } from '@/i18n'
import { transformInputSpecV1ToV2 } from '@/schemas/nodeDef/migration'
import type { InputSpec as InputSpecV2 } from '@/schemas/nodeDef/nodeDefSchemaV2'
import type { InputSpec } from '@/schemas/nodeDefSchema'
import { useSettingStore } from '@/stores/settingStore'

import type { ComfyApp } from './app'
import './domWidget'
import './errorNodeWidgets'

export type ComfyWidgetConstructorV2 = (
  node: LGraphNode,
  inputSpec: InputSpecV2
) => IBaseWidget

export type ComfyWidgetConstructor = (
  node: LGraphNode,
  inputName: string,
  inputData: InputSpec,
  app: ComfyApp,
  widgetName?: string
) => { widget: IBaseWidget; minWidth?: number; minHeight?: number }

/**
 * Transforms a V2 widget constructor to a V1 widget constructor.
 * @param widgetConstructorV2 The V2 widget constructor to transform.
 * @returns The transformed V1 widget constructor.
 */
const transformWidgetConstructorV2ToV1 = (
  widgetConstructorV2: ComfyWidgetConstructorV2
): ComfyWidgetConstructor => {
  return (node, inputName, inputData) => {
    const inputSpec = transformInputSpecV1ToV2(inputData, {
      name: inputName
    })
    const widget = widgetConstructorV2(node, inputSpec)
    return {
      widget,
      minWidth: widget.options.minNodeSize?.[0],
      minHeight: widget.options.minNodeSize?.[1]
    }
  }
}

function controlValueRunBefore() {
  return useSettingStore().get('Comfy.WidgetControlMode') === 'before'
}

export function updateControlWidgetLabel(widget: IBaseWidget) {
  if (controlValueRunBefore()) {
    widget.label = t('g.control_before_generate')
  } else {
    widget.label = t('g.control_after_generate')
  }
}

export const IS_CONTROL_WIDGET = Symbol()
const HAS_EXECUTED = Symbol()

export function addValueControlWidget(
  node: LGraphNode,
  targetWidget: IBaseWidget,
  defaultValue?: string,
  _values?: unknown,
  widgetName?: string,
  inputData?: InputSpec
): IComboWidget {
  let name = inputData?.[1]?.control_after_generate
  if (typeof name !== 'string') {
    name = widgetName
  }
  const widgets = addValueControlWidgets(
    node,
    targetWidget,
    defaultValue ?? 'randomize',
    {
      addFilterList: false,
      controlAfterGenerateName: name
    },
    inputData
  )
  return widgets[0]
}

export function addValueControlWidgets(
  node: LGraphNode,
  targetWidget: IBaseWidget,
  defaultValue?: string,
  options?: Record<string, any>,
  inputData?: InputSpec
): [IComboWidget, ...IStringWidget[]] {
  if (!defaultValue) defaultValue = 'randomize'
  if (!options) options = {}

  const getName = (defaultName: string, optionName: string) => {
    let name = defaultName
    if (options[optionName]) {
      name = options[optionName]
    } else if (typeof inputData?.[1]?.[defaultName] === 'string') {
      name = inputData?.[1]?.[defaultName]
    } else if (inputData?.[1]?.control_prefix) {
      name = inputData?.[1]?.control_prefix + ' ' + name
    }
    return name
  }

  const valueControl = node.addWidget(
    'combo',
    getName('control_after_generate', 'controlAfterGenerateName'),
    defaultValue,
    function () {},
    {
      values: ['fixed', 'increment', 'decrement', 'randomize'],
      serialize: false // Don't include this in prompt.
    }
  ) as IComboWidget

  valueControl.tooltip =
    'Allows the linked widget to be changed automatically, for example randomizing the noise seed.'
  // @ts-ignore index with symbol
  valueControl[IS_CONTROL_WIDGET] = true
  updateControlWidgetLabel(valueControl)
  const widgets: [IComboWidget, ...IStringWidget[]] = [valueControl]

  const isCombo = targetWidget.type === 'combo'
  let comboFilter: IStringWidget
  if (isCombo && valueControl.options.values) {
    // @ts-expect-error Combo widget values may be a dictionary or legacy function type
    valueControl.options.values.push('increment-wrap')
  }
  if (isCombo && options.addFilterList !== false) {
    comboFilter = node.addWidget(
      'string',
      getName('control_filter_list', 'controlFilterListName'),
      '',
      function () {},
      {
        serialize: false // Don't include this in prompt.
      }
    ) as IStringWidget
    updateControlWidgetLabel(comboFilter)
    comboFilter.tooltip =
      "Allows for filtering the list of values when changing the value via the control generate mode. Allows for RegEx matches in the format /abc/ to only filter to values containing 'abc'."

    widgets.push(comboFilter)
  }

  const applyWidgetControl = () => {
    var v = valueControl.value

    if (isCombo && v !== 'fixed') {
      let values = targetWidget.options.values ?? []
      const filter = comboFilter?.value
      if (filter) {
        let check
        if (filter.startsWith('/') && filter.endsWith('/')) {
          try {
            const regex = new RegExp(filter.substring(1, filter.length - 1))
            check = (item: string) => regex.test(item)
          } catch (error) {
            console.error(
              'Error constructing RegExp filter for node ' + node.id,
              filter,
              error
            )
          }
        }
        if (!check) {
          const lower = filter.toLocaleLowerCase()
          check = (item: string) => item.toLocaleLowerCase().includes(lower)
        }
        // @ts-expect-error Combo widget values may be a dictionary or legacy function type
        values = values.filter((item: string) => check(item))
        if (!values.length && targetWidget.options.values?.length) {
          console.warn(
            'Filter for node ' + node.id + ' has filtered out all items',
            filter
          )
        }
      }
      // @ts-expect-error targetWidget.value can be number or string
      let current_index = values.indexOf(targetWidget.value)
      let current_length = values.length

      switch (v) {
        case 'increment':
          current_index += 1
          break
        case 'increment-wrap':
          current_index += 1
          if (current_index >= current_length) {
            current_index = 0
          }
          break
        case 'decrement':
          current_index -= 1
          break
        case 'randomize':
          // @ts-expect-error Combo widget values may be a dictionary or legacy function type
          current_index = Math.floor(Math.random() * current_length)
          break
        default:
          break
      }
      current_index = Math.max(0, current_index)
      // @ts-expect-error Combo widget values may be a dictionary or legacy function type
      current_index = Math.min(current_length - 1, current_index)
      if (current_index >= 0) {
        let value = values[current_index]
        targetWidget.value = value
        targetWidget.callback?.(value)
      }
    } else {
      //number
      let { min = 0, max = 1, step2 = 1 } = targetWidget.options
      // limit to something that javascript can handle
      max = Math.min(1125899906842624, max)
      min = Math.max(-1125899906842624, min)
      let range = (max - min) / step2

      //adjust values based on valueControl Behaviour
      switch (v) {
        case 'fixed':
          break
        case 'increment':
          // @ts-expect-error targetWidget.value can be number or string
          targetWidget.value += step2
          break
        case 'decrement':
          // @ts-expect-error targetWidget.value can be number or string
          targetWidget.value -= step2
          break
        case 'randomize':
          targetWidget.value = Math.floor(Math.random() * range) * step2 + min
          break
        default:
          break
      }
      /*check if values are over or under their respective
       * ranges and set them to min or max.*/
      // @ts-expect-error targetWidget.value can be number or string
      if (targetWidget.value < min) targetWidget.value = min
      // @ts-expect-error targetWidget.value can be number or string
      if (targetWidget.value > max) targetWidget.value = max
      targetWidget.callback?.(targetWidget.value)
    }
  }

  valueControl.beforeQueued = () => {
    if (controlValueRunBefore()) {
      // Don't run on first execution
      // @ts-ignore index with symbol
      if (valueControl[HAS_EXECUTED]) {
        applyWidgetControl()
      }
    }
    // @ts-ignore index with symbol
    valueControl[HAS_EXECUTED] = true
  }

  valueControl.afterQueued = () => {
    if (!controlValueRunBefore()) {
      applyWidgetControl()
    }
  }

  return widgets
}

export const ComfyWidgets: Record<string, ComfyWidgetConstructor> = {
  INT: transformWidgetConstructorV2ToV1(useIntWidget()),
  FLOAT: transformWidgetConstructorV2ToV1(useFloatWidget()),
  BOOLEAN: transformWidgetConstructorV2ToV1(useBooleanWidget()),
  STRING: transformWidgetConstructorV2ToV1(useStringWidget()),
  MARKDOWN: transformWidgetConstructorV2ToV1(useMarkdownWidget()),
  COMBO: transformWidgetConstructorV2ToV1(useComboWidget()),
  IMAGEUPLOAD: useImageUploadWidget(),
  // IMAGEUPLOAD(node: LGraphNode, inputName: string, inputData: InputSpec, app) {
  //   // TODO make image upload handle a custom node type?
  //   const imageWidget = node.widgets?.find(
  //     (w) => w.name === (inputData[1]?.widget ?? 'image')
  //   )
  //   let uploadWidget: any

  //   function showImage(name: string) {
  //     const img = new Image()
  //     img.onload = () => {

  //       (node as any).imgs = [img]
  //       app.graph.setDirtyCanvas(true)
  //     }
  //     // let folder_separator = name.lastIndexOf('/')
  //     // let subfolder = ''
  //     // if (folder_separator > -1) {
  //     //   subfolder = name.substring(0, folder_separator)
  //     //   name = name.substring(folder_separator + 1)
  //     // }
  //     // img.src = api.apiURL(
  //     //   `/view?filename=${encodeURIComponent(name)}&type=input&subfolder=${subfolder}${app.getPreviewFormatParam()}${app.getRandParam()}`
  //     // )
  //     img.src = name
      
  //     node.setSizeForImage?.()
  //   }

  //   var default_value = imageWidget?.value
  //   Object.defineProperty(imageWidget, 'value', {
  //     set: function (value) {
  //       this._real_value = value
  //     },

  //     get: function () {
  //       if (!this._real_value) {
  //         return default_value
  //       }

  //       let value = this._real_value
  //       if (value.filename) {
  //         let real_value = value
  //         value = ''
  //         if (real_value.subfolder) {
  //           value = real_value.subfolder + '/'
  //         }

  //         value += real_value.filename

  //         if (real_value.type && real_value.type !== 'input')
  //           value += ` [${real_value.type}]`
  //       }
  //       return value
  //     }
  //   })

  //   // Add our own callback to the combo widget to render an image when it changes
  //   // TODO: Explain this?
  //   // @ts-expect-error
  //   const cb = node.callback
  //   imageWidget.callback = function () {
  //     showImage(imageWidget.value)
  //     if (cb) {
  //       return cb.apply(this, arguments)
  //     }
  //   }

  //   // On load if we have a value then render the image
  //   // The value isnt set immediately so we need to wait a moment
  //   // No change callbacks seem to be fired on initial setting of the value
  //   requestAnimationFrame(() => {
  //     if (imageWidget.value) {
  //       showImage(imageWidget.value)
  //     }
  //   })

  //   async function uploadFile(file, updateNode, pasted = false) {
	// 		try {
	// 			//检查一下是否登录，没登录不能上传图片
	// 			let userInfo = (top as any).localStorage.getItem('userInfo');
	// 			userInfo = userInfo && JSON.parse(userInfo);
	// 			if (!userInfo || !userInfo?.id) {
	// 				(top as any)?.$EventBus?.$emit("openLoginDialog");
	// 				return;
	// 			}
	// 			let {width, height} = await checkImageSize(file);
	// 			let UPLOAD = (top as any)?.workflow_upload;
	// 			UPLOAD
	// 				.startUpload(file, {category: 20, sub_category: 1})
	// 				.then(url => {
	// 					if (!imageWidget.options.values.includes(url)) {
	// 						imageWidget.options.values.push(url);
	// 						// todo 这里是和后端约定的值，将图片真实宽高传给了后端
	// 						uploadWidget.value = `${width}@${height}`
	// 					}

	// 					if (updateNode) {
	// 						showImage(url);
	// 						imageWidget.value = url;
	// 					}
	// 				})
	// 				.catch(err => {
  //           console.error(err);
	// 				});
	// 		} catch (error) {
  //       console.error(error);
	// 		}
	// 	}

  //   // 读取图片宽高
	// 	async function checkImageSize(file: File): Promise<ImageSize> {
	// 		return new Promise((resolve,reject)=>{
	// 			let reader = new FileReader();
	// 			reader.onload = function(event) {
	// 				let img:any = new Image();
	// 				img.onload = function() {
	// 					let width = img.naturalWidth;
	// 					let height = img.naturalHeight;
	// 					resolve({width, height});
	// 				};
	// 				img.onerror = function (){
	// 					reject(false);
	// 				}
	// 				img.src = event.target.result;
	// 			};
	// 			reader.readAsDataURL(file);
	// 		});
	// 	}

  //   const fileInput = document.createElement('input')
  //   Object.assign(fileInput, {
  //     type: 'file',
  //     accept: 'image/jpeg,image/png,image/webp',
  //     style: 'display: none',
  //     onchange: async () => {
  //       if (fileInput.files.length) {
  //         await uploadFile(fileInput.files[0], true)
  //       }
  //     }
  //   })
  //   document.body.append(fileInput)

  //   // Create the button widget for selecting the files
  //   uploadWidget = node.addWidget('button', inputName, 'image', () => {
  //     fileInput.click(),
  //     {
  //       serialize: false
  //     }
  //   })
  //   uploadWidget.label = t('g.choose_file_to_upload')
  //   // uploadWidget.serialize = false

  //   // Add handler to check if an image is being dragged over our node
  //   // @ts-expect-error
  //   node.onDragOver = function (e) {
  //     if (e.dataTransfer && e.dataTransfer.items) {
  //       const image = [...e.dataTransfer.items].find((f) => f.kind === 'file')
  //       return !!image
  //     }

  //     return false
  //   }

  //   // On drop upload files
  //   // @ts-expect-error
  //   node.onDragDrop = function (e) {
  //     console.log('onDragDrop called')
  //     let handled = false
  //     for (const file of e.dataTransfer.files) {
  //       if (file.type.startsWith('image/')) {
  //         uploadFile(file, !handled) // Dont await these, any order is fine, only update on first one
  //         handled = true
  //       }
  //     }

  //     return handled
  //   }

  //   // @ts-expect-error
  //   node.pasteFile = function (file) {
  //     if (file.type.startsWith('image/')) {
  //       const is_pasted =
  //         file.name === 'image.png' && file.lastModified - Date.now() < 2000
  //       uploadFile(file, true, is_pasted)
  //       return true
  //     }
  //     return false
  //   }

  //   return { widget: uploadWidget }
  // },
  MODEL_SELECT(node: LGraphNode, inputName: string, inputData: InputSpec, app: ComfyApp) {
    // 使用类型断言来处理类型不匹配的问题
    const typedNode = node as any;
    // 更新选择器，适应可能变化的 widget 结构
    const ckptNameWidgets = typedNode.widgets?.filter(w => 
      w.options?.values?.includes('checkpoints_blank.safetensors') || 
      (w.name && (w.name.includes('ckpt') || w.name.includes('checkpoint') || w.name.includes('model')))
    );
    
    if (!ckptNameWidgets || ckptNameWidgets.length === 0) {
      // 尝试另一种方式找到 checkpoint widget
      const possibleWidgets = typedNode.widgets?.filter(w => 
        w.type === 'combo' && 
        (w.name?.toLowerCase().includes('model') || w.name?.toLowerCase().includes('checkpoint'))
      );
      
      if (possibleWidgets && possibleWidgets.length > 0) {
        for (const widget of possibleWidgets) {
          setupCheckpointWidget(widget, typedNode);
        }
        return { widget: null };
      }
      
      console.log('无法找到checkpoint小部件:', typedNode);
      return { widget: null };
    }
    
    const ckptIndexs = getCkptIndexs(typedNode);
    const loraIndexs = getLoraIndexs(typedNode);
    const origOnConfigure = typedNode.onConfigure;
    
    if (!typedNode.ModelSerialize) {
      typedNode.onConfigure = onConfigureModelSerialize(origOnConfigure, [...ckptIndexs, ...loraIndexs]);
      typedNode.ModelSerialize = true;
    }
    
    for (const ckptWidget of ckptNameWidgets) {
      setupCheckpointWidget(ckptWidget, typedNode);
    }
    
    return { widget: null };
    
    // 辅助函数，设置checkpoint部件
    function setupCheckpointWidget(ckptNameWidget: any, node: any) {
      ckptNameWidget.disabled = false;
      
      // 保存原始的处理方法
      const originalCallback = ckptNameWidget.callback;
      
      // 重写callback，以便在点击时触发我们的选择器
      ckptNameWidget.callback = function(...args: any[]) {
        // 调用检查点选择器
        pickCheckpoint();
        
        // 如果有原始回调，也执行它
        if (originalCallback) {
          return originalCallback.apply(this, args);
        }
      };
      
      async function checkpointPicker() {
        try {
          const param = {
            type: "openWorkflowSelectModel",
            info: {
              modelType: 'Checkpoint',
              ckptNameWidget: JSON.stringify({
                id: ckptNameWidget.id || node.id,
                name: ckptNameWidget.name
              }),
              node: node.id,
            }
          };
          top.postMessage(param, '*');
        } catch (e) {
          console.error("打开模型选择器出错:", e);
          return null;
        }
      }
      
      async function pickCheckpoint() {
        await checkpointPicker();
        try {
          // 添加事件处理函数，处理模型选择后的回调
          ckptNameWidget.onModelPicked = function(id: string, name: string) {
            console.log("模型已选择:", id, name);
            ckptNameWidget.value = name;
            if (ckptNameWidget.options) {
              ckptNameWidget.options.display_values = [name];
              ckptNameWidget.options.display_value = name;
            }
            ckptNameWidget.serializeValue = () => {
              return id;
            };
            // 触发图形重绘
            if (app && app.graph) {
              app.graph.setDirtyCanvas(true);
            }
          };
        } catch (e) {
          console.error("设置模型选择回调失败:", e);
        }
      }
      
      // 确保values函数按预期工作
      if (!ckptNameWidget.options) {
        ckptNameWidget.options = {};
      }
      
      ckptNameWidget.options.values = (w: any, node: any) => {
        if (w && node) {
          pickCheckpoint();
        }
        return [];  // 返回空数组，防止显示默认下拉菜单
      };
      
      // 保存原始值列表
      if (Array.isArray(ckptNameWidget.options.values)) {
        ckptNameWidget.options._values = [...ckptNameWidget.options.values];
        ckptNameWidget.options.values = function(w: any, node: any) {
          if (w && node) {
            pickCheckpoint();
          }
          return [];  // 返回空数组，防止显示默认下拉菜单
        };
      }
      
      ckptNameWidget.options.values.includes = () => true;
    }
  },
  LORA_SELECT(node: LGraphNode, inputName: string, inputData: InputSpec, app: ComfyApp) {
    // 使用类型断言来处理类型不匹配的问题
    const typedNode = node as any;
    // 更新选择器，适应可能变化的widget结构
    const loraNameWidgets = typedNode.widgets?.filter(w => 
      w.options?.values?.includes('lora_blank.safetensors') || 
      (w.name && w.name.toLowerCase().includes('lora'))
    );
    
    if (!loraNameWidgets || loraNameWidgets.length === 0) {
      // 尝试另一种方式找到lora widget
      const possibleWidgets = typedNode.widgets?.filter(w => 
        w.type === 'combo' && w.name?.toLowerCase().includes('lora')
      );
      
      if (possibleWidgets && possibleWidgets.length > 0) {
        for (const widget of possibleWidgets) {
          setupLoraWidget(widget, typedNode);
        }
        return { widget: null };
      }
      
      console.log('无法找到lora小部件:', typedNode);
      return { widget: null };
    }
    
    const ckptIndexs = getCkptIndexs(typedNode);
    const loraIndexs = getLoraIndexs(typedNode);
    const origOnConfigure = typedNode.onConfigure;
    
    if (!typedNode.ModelSerialize) {
      typedNode.onConfigure = onConfigureModelSerialize(origOnConfigure, [...ckptIndexs, ...loraIndexs]);
      typedNode.ModelSerialize = true;
    }
    
    for (const loraWidget of loraNameWidgets) {
      setupLoraWidget(loraWidget, typedNode);
    }
    
    return { widget: null };
    
    // 辅助函数，设置lora部件
    function setupLoraWidget(loraNameWidget: any, node: any) {
      loraNameWidget.disabled = false;
      
      // 保存原始的处理方法
      const originalCallback = loraNameWidget.callback;
      
      // 重写callback，以便在点击时触发我们的选择器
      loraNameWidget.callback = function(...args: any[]) {
        // 调用lora选择器
        pickLora();
        
        // 如果有原始回调，也执行它
        if (originalCallback) {
          return originalCallback.apply(this, args);
        }
      };
      
      async function loraPicker() {
        try {
          const param = {
            type: "openWorkflowSelectModel",
            info: {
              modelType: 'LORA',
              ckptNameWidget: JSON.stringify({
                id: loraNameWidget.id || node.id,
                name: loraNameWidget.name
              }),
              node: node.id,
            }
          };
          top.postMessage(param, '*');
        } catch (e) {
          console.error("打开lora选择器出错:", e);
          return null;
        }
      }
      
      async function pickLora() {
        await loraPicker();
        try {
          // 添加事件处理函数，处理lora选择后的回调
          loraNameWidget.onLoraPicked = function(id: string, name: string) {
            console.log("Lora已选择:", id, name);
            loraNameWidget.value = name;
            if (loraNameWidget.options) {
              loraNameWidget.options.display_values = [name];
              loraNameWidget.options.display_value = name;
            }
            loraNameWidget.serializeValue = () => {
              return id;
            };
            // 触发图形重绘
            if (app && app.graph) {
              app.graph.setDirtyCanvas(true);
            }
          };
        } catch (e) {
          console.error("设置lora选择回调失败:", e);
        }
      }
      
      // 确保values函数按预期工作
      if (!loraNameWidget.options) {
        loraNameWidget.options = {};
      }
      
      // 保存原始值列表
      if (Array.isArray(loraNameWidget.options.values)) {
        loraNameWidget.options._values = [...loraNameWidget.options.values];
        loraNameWidget.options.values = function(w: any, node: any) {
          if (w && node) {
            pickLora();
          }
          return [];  // 返回空数组，防止显示默认下拉菜单
        };
      }
      
      loraNameWidget.options.values = (w: any, node: any) => {
        if (w && node) {
          pickLora();
        }
        return [];  // 返回空数组，防止显示默认下拉菜单
      };
      
      loraNameWidget.options.values.includes = () => true;
    }
  },
  EMBEDDINGS_SELECT(node: LGraphNode, inputName: string, inputData: InputSpec, app: ComfyApp) {
    // 使用类型断言来处理类型不匹配的问题
    const typedNode = node as any;
    // 更新选择器，适应可能变化的widget结构
    const embeddingsNameWidgets = typedNode.widgets?.filter(w => 
      w.options?.values?.includes('embeddings_blank.safetensors') || 
      (w.name && w.name.toLowerCase().includes('embedding'))
    );
    
    if (!embeddingsNameWidgets || embeddingsNameWidgets.length === 0) {
      // 尝试另一种方式找到embeddings widget
      const possibleWidgets = typedNode.widgets?.filter(w => 
        w.type === 'combo' && 
        (w.name?.toLowerCase().includes('embedding') || w.name?.toLowerCase().includes('embeddings') || w.name?.toLowerCase().includes('textual_inversion'))
      );
      
      if (possibleWidgets && possibleWidgets.length > 0) {
        for (const widget of possibleWidgets) {
          setupEmbeddingsWidget(widget, typedNode);
        }
        return { widget: null };
      }
      
      console.log('无法找到embeddings小部件:', typedNode);
      return { widget: null };
    }
    
    const ckptIndexs = getCkptIndexs(typedNode);
    const loraIndexs = getLoraIndexs(typedNode);
    const embeddingsIndexs = getEmbeddingsIndexs(typedNode);
    const origOnConfigure = typedNode.onConfigure;
    
    if (!typedNode.ModelSerialize) {
      typedNode.onConfigure = onConfigureModelSerialize(origOnConfigure, [...ckptIndexs, ...loraIndexs, ...embeddingsIndexs]);
      typedNode.ModelSerialize = true;
    }
    
    for (const embeddingsWidget of embeddingsNameWidgets) {
      setupEmbeddingsWidget(embeddingsWidget, typedNode);
    }
    
    return { widget: null };
    
    // 辅助函数，设置embeddings部件
    function setupEmbeddingsWidget(embeddingsNameWidget: any, node: any) {
      embeddingsNameWidget.disabled = false;
      
      // 保存原始的处理方法
      const originalCallback = embeddingsNameWidget.callback;
      
      // 重写callback，以便在点击时触发我们的选择器
      embeddingsNameWidget.callback = function(...args: any[]) {
        // 调用embeddings选择器
        pickEmbeddings();
        
        // 如果有原始回调，也执行它
        if (originalCallback) {
          return originalCallback.apply(this, args);
        }
      };
      
      async function embeddingsPicker() {
        try {
          const param = {
            type: "openWorkflowSelectModel",
            info: {
              modelType: 'TextualInversion',
              ckptNameWidget: JSON.stringify({
                id: embeddingsNameWidget.id || node.id,
                name: embeddingsNameWidget.name
              }),
              node: node.id,
            }
          };
          top.postMessage(param, '*');
        } catch (e) {
          console.error("打开embeddings选择器出错:", e);
          return null;
        }
      }
      
      async function pickEmbeddings() {
        await embeddingsPicker();
        try {
          // 添加事件处理函数，处理embeddings选择后的回调
          embeddingsNameWidget.onEmbeddingsPicked = function(id: string, name: string) {
            console.log("Embeddings已选择:", id, name);
            embeddingsNameWidget.value = name;
            if (embeddingsNameWidget.options) {
              embeddingsNameWidget.options.display_values = [name];
              embeddingsNameWidget.options.display_value = name;
            }
            embeddingsNameWidget.serializeValue = () => {
              return id;
            };
            // 触发图形重绘
            if (app && app.graph) {
              app.graph.setDirtyCanvas(true);
            }
          };
        } catch (e) {
          console.error("设置embeddings选择回调失败:", e);
        }
      }
      
      // 确保values函数按预期工作
      if (!embeddingsNameWidget.options) {
        embeddingsNameWidget.options = {};
      }
      
      // 保存原始值列表
      if (Array.isArray(embeddingsNameWidget.options.values)) {
        embeddingsNameWidget.options._values = [...embeddingsNameWidget.options.values];
        embeddingsNameWidget.options.values = function(w: any, node: any) {
          if (w && node) {
            pickEmbeddings();
          }
          return [];  // 返回空数组，防止显示默认下拉菜单
        };
      }
      
      embeddingsNameWidget.options.values = (w: any, node: any) => {
        if (w && node) {
          pickEmbeddings();
        }
        return [];  // 返回空数组，防止显示默认下拉菜单
      };
      
      embeddingsNameWidget.options.values.includes = () => true;
    }
  }
}

function getCkptIndexs(node: any) {
  const ckptIndexs = [];
  if (!node.widgets) return ckptIndexs;
  
  for (let i = 0; i < node.widgets.length; ++i) {
      if (node.widgets[i].options?.values?.includes('checkpoints_blank.safetensors')) {
          ckptIndexs.push(i);
      }
  }
  return ckptIndexs
}

function getLoraIndexs(node: any) {
  const loraIndexs = [];
  if (!node.widgets) return loraIndexs;
  
  for (let i = 0; i < node.widgets.length; ++i) {
      if (node.widgets[i].options?.values?.includes('lora_blank.safetensors')) {
          loraIndexs.push(i);
      }
  }
  return loraIndexs
}

function getEmbeddingsIndexs(node: any) {
  const embeddingsIndexs = [];
  if (!node.widgets) return embeddingsIndexs;
  
  for (let i = 0; i < node.widgets.length; ++i) {
      if (node.widgets[i].options?.values?.includes('embeddings_blank.safetensors')) {
          embeddingsIndexs.push(i);
      }
  }
  return embeddingsIndexs
}

function onConfigureModelSerialize(origOnConfigure: any, modelIndexs: number[]) {
  return function(this: any, info: any) {
      const r = origOnConfigure ? origOnConfigure.apply(this, arguments) : undefined;
      if (!info.serialize_values) {
          return
      }
      for (var i = 0; i < info.serialize_values.length; ++i) {
          if (!this.widgets[i] || !info.serialize_values[i]) {
              continue
          }
          if (modelIndexs.indexOf(i) == -1) {
              continue
          }
          let serialVal = info.serialize_values[i]
          this.widgets[i].serializeValue = () => {
              return serialVal;
          };
      }
  }
}